import React, {
  RefObject,
  useCallback,
  useEffect,
  useState,
  useTransition,
} from "react";
import PathPickSectionCard from "@/features/pathpick/components/ui/PathPickSectionCard";
import PathPickSearchProductsProductListHeader from "@/features/pathpick/features/search-products/components/product-list/PathPickSearchProductsProductListHeader";
import PathPickSearchProductsProductListContent from "@/features/pathpick/features/search-products/components/product-list/PathPickSearchProductsProductListContent";
import PathPickSearchProductsSelectionBar from "@/features/pathpick/features/search-products/components/PathPickSearchProductsSelectionBar";
import PathPickPagination from "@/features/pathpick/components/ui/PathPickPagination";
import PathPickProductCardGridSkeleton from "@/features/pathpick/components/product/PathPickProductCardGridSkeleton";
import PathPickScrollToTopButton from "@/features/pathpick/components/ui/PathPickScrollToTopButton";
import PathPickSelectionBarSpacer from "@/features/pathpick/components/ui/PathPickSelectionBarSpacer";
import CustomNoResults from "@/shared/components/no-results/CustomNoResults";
import {
  KreamBuyerInfo,
  PathPickProductListViewType,
} from "@/features/pathpick/features/search-products/types";
import {
  PathPickMarketPriceBasis,
  PathPickMutationProduct,
  PathPickProduct,
  PathPickProductKey,
} from "@/features/pathpick/types";
import { toPathPickProductKey } from "@/features/pathpick/utils";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import usePathPickProductFilter from "@/features/pathpick/features/search-products/hooks/usePathPickProductFilter";
import useGetPathPickSearchProducts, {
  buildPathPickSearchProductsRequestParams,
} from "@/features/pathpick/features/search-products/hooks/query/useGetPathPickSearchProducts";
import usePathPickAllResultIds from "@/features/pathpick/hooks/usePathPickAllResultIds";
import usePathPickSyncKinds from "@/features/pathpick/hooks/usePathPickSyncKinds";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { getHttpStatus, toErrorResponse } from "@/utils/axios-client-error";
import useAddPathPickBasket from "@/features/pathpick/features/search-products/hooks/mutation/useAddPathPickBasket";
import useLocalStorageState from "@/shared/hooks/useLocalStorageState";
import { PathPickHotDealLineSheet } from "@/features/pathpick/types";

interface Props {
  selectedLineSheet: PathPickHotDealLineSheet | null;
  onClearSelectedLineSheet: () => void;
  // 페이지의 스크롤 Box — "맨 위로" 버튼이 이 요소를 직접 올린다.
  scrollContainerRef: RefObject<HTMLDivElement>;
}

// TODO: 선택된 라인시트 기준 상품 필터 API 파라미터 연결
function PathPickSearchProductsProductListSection({
  selectedLineSheet,
  onClearSelectedLineSheet,
  scrollContainerRef,
}: Props) {
  // 선택한 뷰 타입을 localStorage에 저장해 재방문 시 복원한다.
  const [viewType, setViewType] =
    useLocalStorageState<PathPickProductListViewType>(
      "pathpick:search-products:view-type",
      "card"
    );
  // 선택 상태 — 키는 "{type}:{id}"(두 유형의 같은 숫자 id가 합쳐지지 않게), 값은 담기 body 항목 그대로.
  const [selectedProducts, setSelectedProducts] = useState<
    Map<PathPickProductKey, PathPickMutationProduct>
  >(new Map());
  const [kreamBuyerInfo, setKreamBuyerInfo] = useState<KreamBuyerInfo>({
    level: 4,
    productType: "general",
  });

  const { t } = useI18nHelper();
  // 페이지네이션도 필터와 같은 쿼리스트링에 있어, 필터 변경 시의 페이지 초기화는
  // 각 액션이 직접 처리한다(별도의 동기화 effect가 필요 없다).
  const {
    filter,
    pageNumber,
    pageSize,
    changeMarketPriceBasis,
    setPageNumber,
    setPageSize,
  } = usePathPickProductFilter();
  const { mutate: addBasket } = useAddPathPickBasket();
  // 결과 전체 id 조회가 목록 조회와 같은 조건을 쓰도록 목록 훅과 같은 플래그를 넘긴다.
  const syncKinds = usePathPickSyncKinds();

  // 비교 기준 — 쿼리스트링으로 관리되며 디폴트(입찰가 기준)는 필터 훅이 보장한다.
  const marketPriceBasis: PathPickMarketPriceBasis =
    filter.marketPriceBasis ?? "bidPrice";

  const {
    pathPickProductList,
    isPending,
    isPlaceholderData,
    error: productsError,
  } = useGetPathPickSearchProducts({
    filter,
    pageNumber,
    pageSize,
  });
  // 검색 호출 제한(429)에 걸리면 목록은 직전 결과가 그대로 남는다 — 왜 안 바뀌는지 서버 문구로 알린다.
  const { error: showError } = useAppToasts({ id: "GLOBAL" });
  useEffect(() => {
    if (getHttpStatus(productsError) === 429) {
      showError(toErrorResponse(productsError));
    }
  }, [productsError, showError]);
  // 초기 로드(isPending)와 필터/페이지 전환(isPlaceholderData) 모두 카드 스켈레톤으로 대체한다.
  // 전환 중에는 직전 목록 개수만큼 그려 레이아웃 시프트를 방지한다.
  const items = pathPickProductList?.products ?? [];
  const total = pathPickProductList?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // 페이지를 유지한 채 결과 수가 줄면(예: KREAM 기준 변경으로 매칭 상품이 빠지는 경우)
  // 존재하지 않는 페이지에 남아 빈 목록이 보이므로 마지막 페이지로 당긴다.
  // 로딩 중에는 이전 응답의 total과 새 pageNumber를 비교하게 되므로 건너뛴다.
  useEffect(() => {
    if (isPending || isPlaceholderData) {
      return;
    }
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [isPending, isPlaceholderData, pageNumber, totalPages, setPageNumber]);

  // 전체 선택은 "현재 페이지" 기준, 선택 자체(selectedProducts)는 페이지 이동해도 유지된다.
  // 이미 담긴(isInBasket) 상품은 선택 대상에서 제외한다.
  const selectableItems = items.filter((item) => !item.isInBasket);
  // 고를 상품이 없으면(빈 결과·페이지 전체가 담긴 상품) 이 페이지 선택을 비활성화한다.
  const isPageSelectable = selectableItems.length > 0;
  const pageSelectedCount = selectableItems.filter((item) =>
    selectedProducts.has(toPathPickProductKey(item.productType, item.id))
  ).length;
  const isAllSelected =
    selectableItems.length > 0 && pageSelectedCount === selectableItems.length;
  const isIndeterminate = pageSelectedCount > 0 && !isAllSelected;

  // 검색 결과 전체 선택 — ids_only 응답엔 담김 여부가 없어, 페이지 선택과 같은 규칙(이미 담긴 상품 제외)을
  // isInBasket: false로 서버에 맡긴다. ids를 미리 받아 "전체 n개 선택"의 n으로 쓰고,
  // 1만 건(ids_only 상한)을 넘어도 받은 1만 건까지는 선택할 수 있게 노출한다.
  const {
    resultIds,
    isTruncated: isAllResultsTruncated,
    isFetching: isFetchingResultIds,
  } = usePathPickAllResultIds(
    {
      ...buildPathPickSearchProductsRequestParams({
        filter,
        pageNumber,
        pageSize,
        syncKinds,
      }),
      isInBasket: false,
    },
    { enabled: true }
  );
  const canSelectAllResults = resultIds !== null && resultIds.length > 0;
  const isAllResultsSelected =
    resultIds !== null &&
    resultIds.length > 0 &&
    resultIds.every(({ id, productType }) =>
      selectedProducts.has(toPathPickProductKey(productType, id))
    );
  // 일부만(이 페이지 선택·카드 개별 선택) 골랐으면 "-"로 보여주고, 누르면 전체를 선택한다.
  const isAllResultsIndeterminate =
    !isAllResultsSelected && selectedProducts.size > 0;

  // 전체 선택/해제는 페이지의 카드 전부를 다시 그려 무겁다 — transition으로 미뤄
  // 그동안 체크박스 위 스피너가 먼저 뜨게 한다(조회 중과 같은 표기).
  const [isApplyingSelection, startSelectionTransition] = useTransition();
  const isAllResultsBusy = isFetchingResultIds || isApplyingSelection;

  const handleToggleAllResults = () => {
    if (isAllResultsBusy || !resultIds) {
      return;
    }
    if (isAllResultsSelected) {
      startSelectionTransition(() => {
        setSelectedProducts((prev) => {
          const next = new Map(prev);
          resultIds.forEach(({ id, productType }) =>
            next.delete(toPathPickProductKey(productType, id))
          );
          return next;
        });
      });
      return;
    }
    startSelectionTransition(() => {
      setSelectedProducts((prev) => {
        const next = new Map(prev);
        resultIds.forEach(({ id, productType }) =>
          next.set(toPathPickProductKey(productType, id), {
            id,
            type: productType,
          })
        );
        return next;
      });
    });
  };

  const handleTogglePage = () => {
    setSelectedProducts((prev) => {
      const next = new Map(prev);
      // 현재 페이지가 전부 선택돼 있을 때만 해제하고, 비었거나 일부만(indeterminate)
      // 선택돼 있으면 전부 선택한다 — 검색 결과 전체 선택과 같은 규칙.
      if (isAllSelected) {
        selectableItems.forEach((item) =>
          next.delete(toPathPickProductKey(item.productType, item.id))
        );
      } else {
        selectableItems.forEach((item) =>
          next.set(toPathPickProductKey(item.productType, item.id), {
            id: item.id,
            type: item.productType,
          })
        );
      }
      return next;
    });
  };

  // 카드(memo)로 내려가는 핸들러 — identity가 고정돼야 카드가 선택 변경에 함께 리렌더되지 않는다.
  const handleToggleItem = useCallback((item: PathPickProduct) => {
    setSelectedProducts((prev) => {
      const next = new Map(prev);
      const key = toPathPickProductKey(item.productType, item.id);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, { id: item.id, type: item.productType });
      }
      return next;
    });
  }, []);

  const handleClear = () => setSelectedProducts(new Map());

  const handlePickItem = useCallback(
    (item: PathPickProduct) => {
      addBasket({ products: [{ id: item.id, type: item.productType }] });
    },
    [addBasket]
  );

  const handleBulkPick = () => {
    if (selectedProducts.size === 0) {
      return;
    }
    addBasket(
      {
        products: Array.from(selectedProducts.values()),
      },
      { onSuccess: () => setSelectedProducts(new Map()) }
    );
  };

  const handleChangePage = (page: number) => {
    setPageNumber(Math.min(Math.max(page, 1), totalPages));
  };

  // 페이지 크기 변경 시의 1페이지 복귀는 setPageSize가 함께 처리한다.
  const handleChangePageSize = (size: number) => {
    setPageSize(size);
  };

  return (
    <>
      <PathPickSectionCard
        isFooterSticky
        header={
          <PathPickSearchProductsProductListHeader
            viewType={viewType}
            onChangeViewType={setViewType}
            isPageSelectable={isPageSelectable}
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
            selectedCount={selectedProducts.size}
            canSelectAllResults={canSelectAllResults}
            allResultsCount={resultIds?.length ?? 0}
            isAllResultsTruncated={isAllResultsTruncated}
            isAllResultsSelected={isAllResultsSelected}
            isAllResultsIndeterminate={isAllResultsIndeterminate}
            isFetchingAllResults={isAllResultsBusy}
            onTogglePage={handleTogglePage}
            onToggleAllResults={handleToggleAllResults}
            onBulkPick={handleBulkPick}
            selectedLineSheet={selectedLineSheet}
            onClearSelectedLineSheet={onClearSelectedLineSheet}
            marketPriceBasis={marketPriceBasis}
            onChangeMarketPriceBasis={changeMarketPriceBasis}
            kreamBuyerInfo={kreamBuyerInfo}
            onSaveKreamBuyerInfo={setKreamBuyerInfo}
          />
        }
        footer={
          <PathPickPagination
            total={total}
            pageNumber={pageNumber}
            pageSize={pageSize}
            // sticky footer가 기준 박스라 absolute면 닫힌 팝오버가 스크롤 영역을 늘린다.
            popoverStrategy={"fixed"}
            scrollContainerRef={scrollContainerRef}
            onChangePage={handleChangePage}
            onChangePageSize={handleChangePageSize}
          />
        }
      >
        {/* 로딩 중에는 카드 스켈레톤이 자리를 지킨다. 리스트뷰에도 카드 그리드 스켈레톤을
            쓰는데, 뷰 전환 UI가 헤더에서 비활성이라 실제로 보이는 건 카드뷰뿐이다. */}
        {isPending || isPlaceholderData ? (
          <PathPickProductCardGridSkeleton count={items.length || undefined} />
        ) : items.length === 0 ? (
          <CustomNoResults
            title={t("path_pick:search_products.product_list.no_results_title")}
            description={t(
              "path_pick:search_products.product_list.no_results_description"
            )}
          />
        ) : (
          <>
            <PathPickSearchProductsProductListContent
              marketPriceBasis={marketPriceBasis}
              viewType={viewType}
              items={items}
              selectedProducts={selectedProducts}
              onToggleItem={handleToggleItem}
              onPickItem={handlePickItem}
            />
            {/* 마지막 행 다음 줄 가운데 — 바닥까지 내렸을 때만 나타난다. */}
            <PathPickScrollToTopButton containerRef={scrollContainerRef} />
            {/* 플로팅 선택 바가 떠 있으면 끝까지 내려도 마지막 카드와 "맨 위로"가 가리지 않게 비운다. */}
            {selectedProducts.size > 0 && <PathPickSelectionBarSpacer />}
          </>
        )}
      </PathPickSectionCard>
      {selectedProducts.size > 0 && (
        <PathPickSearchProductsSelectionBar
          selectedCount={selectedProducts.size}
          onClear={handleClear}
          onBulkPick={handleBulkPick}
        />
      )}
    </>
  );
}

export default PathPickSearchProductsProductListSection;
